import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import {
  formatDateLabel,
  getCinemaById,
  getMovieById,
  getScheduleById,
} from "@/lib/data";

export default function ScheduleDetailPage() {
  const { id = "" } = useParams();
  const schedule = getScheduleById(id);
  const movie = schedule ? getMovieById(schedule.movieId) : undefined;
  const cinema = schedule ? getCinemaById(schedule.cinemaId) : undefined;
  const [showGuide, setShowGuide] = useState(false);

  if (!schedule || !movie || !cinema) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">场次不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">该排片场次可能已结束或链接有误。</p>
          <Link
            to="/cinemas"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回影院排片
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const details = [
    { label: "放映日期", value: formatDateLabel(schedule.date), icon: "ri-calendar-line" },
    { label: "放映时间", value: schedule.time, icon: "ri-time-line" },
    { label: "影厅", value: schedule.hall, icon: "ri-film-line" },
    { label: "语言版本", value: schedule.language, icon: "ri-global-line" },
    { label: "放映制式", value: schedule.format, icon: "ri-hd-line" },
    { label: "票价", value: `¥${schedule.price} / 张`, icon: "ri-money-cny-circle-line" },
  ];

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影院排片", to: "/cinemas" },
            { label: cinema.name, to: `/cinema/${cinema.id}` },
            { label: "排片详情" },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex flex-col gap-5 sm:flex-row">
                <Link to={`/movie/${movie.id}`} className="w-28 shrink-0">
                  <div className="aspect-[2/3] w-full overflow-hidden rounded-md bg-background-200">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} 九七电影院`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                </Link>
                <div className="flex-1">
                  <h1 className="font-heading text-2xl font-bold text-foreground-950">
                    {movie.title}
                  </h1>
                  <p className="mt-2 text-sm text-foreground-600">
                    {movie.genres.join(" / ")} · {movie.duration} 分钟 · {movie.language}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                      {movie.year} 年
                    </span>
                    <span className="rounded-full bg-accent-100 px-3 py-1 text-accent-900">
                      评分 {movie.rating.toFixed(1)}
                    </span>
                  </div>
                  <p className="mt-4 flex flex-wrap items-center gap-1 text-sm text-foreground-700">
                    <i className="ri-film-line text-accent-600"></i>
                    放映影院：
                    <Link to={`/cinema/${cinema.id}`} className="font-medium text-primary-600 hover:underline">
                      {cinema.name}
                    </Link>
                  </p>
                  <p className="mt-1 flex items-start gap-1 text-sm text-foreground-500">
                    <i className="ri-map-pin-line mt-0.5"></i>
                    {cinema.address}
                  </p>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {details.map((item) => (
                  <div key={item.label} className="rounded-md bg-background-100 p-3">
                    <p className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className={`${item.icon} text-accent-600`}></i>
                      {item.label}
                    </p>
                    <p className="mt-1 font-heading text-base font-semibold text-foreground-950">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-error-warning-line text-accent-600"></i>
                退订及换票政策
              </h2>
              <ul className="mt-3 space-y-2 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-foreground-400"></i>
                  开场前 2 小时可申请退票，退票将收取票面金额 10% 的手续费。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-foreground-400"></i>
                  每笔订单可免费换票一次，需在开场前 1 小时完成操作。
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-foreground-400"></i>
                  特殊影厅（IMAX、4DX 等）一经售出恕不退换，请仔细确认场次信息。
                </li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-sm text-foreground-500">应付票价</p>
              <p className="mt-1 font-heading text-3xl font-bold text-accent-600">
                ¥{schedule.price}
              </p>
              <p className="mt-1 text-xs text-foreground-400">价格以影院实际收取为准</p>

              <button
                type="button"
                onClick={() => setShowGuide(true)}
                className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-shopping-bag-3-line"></i>立即购票
              </button>
              <Link
                to={`/cinema/${cinema.id}`}
                className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-list-check"></i>查看影院其它场次
              </Link>

              {showGuide && (
                <div className="mt-5 rounded-md border border-accent-200 bg-accent-50 p-4 text-sm text-accent-900">
                  <p className="flex items-center gap-2 font-medium">
                    <i className="ri-information-line"></i>购票指引
                  </p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs leading-relaxed">
                    <li>选择影片、影院与场次，确认座位区域。</li>
                    <li>登录或注册购票账号，选择支付方式。</li>
                    <li>支付成功后凭取票码到影院自助机取票。</li>
                  </ol>
                  <p className="mt-2 text-xs text-accent-800">
                    温馨提示：本站为信息演示站点，不提供实时在线支付，实际购票请以影院官方渠道为准。
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowGuide(false)}
                    className="mt-2 text-xs font-medium text-accent-700 underline"
                  >
                    我知道了
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}